import { useCallback, useMemo, useState } from 'react';
import GlobalEmptyState from '../components/GlobalEmptyState';
import GlobalSidebar from '../components/GlobalSidebar';
import { GlobalButton } from '../components/GlobalButton';
import { GlobalMicroButton } from '../components/GlobalMicroButton';
import { GlobalSortableTh } from '../components/GlobalSortableTh';
import { LegalEntityForm, type LegalEntityFormValues } from '../components/LegalEntityForm';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { archiveLegalEntity, saveLegalEntity } from '../lib/saveLegalEntity';
import { computeGroupQualification, type GroupQualification } from '../utils/groupGraph';
import { formatBpsAsPercent } from '../utils/bps';
import { compareSortValues, useTableSort } from '../hooks/useTableSort';
import type { LegalEntity } from '../types';
import styles from './EntitiesView.module.css';

type TreeRow = { entity: LegalEntity; depth: number };

type EntitySortKey = 'name' | 'legalForm' | 'seat' | 'ownership' | 'group';

/**
 * Flattens the entity forest depth-first: roots first, children indented.
 * `compare` orders siblings within each level so column sorting keeps the
 * parent/child structure intact.
 */
function buildTreeRows(
  entities: LegalEntity[],
  compare: (a: LegalEntity, b: LegalEntity) => number
): TreeRow[] {
  const ids = new Set(entities.map((e) => e.id));
  const childrenByParent = new Map<string, LegalEntity[]>();
  const roots: LegalEntity[] = [];
  for (const entity of entities) {
    if (entity.parentEntityId && ids.has(entity.parentEntityId)) {
      const list = childrenByParent.get(entity.parentEntityId) ?? [];
      list.push(entity);
      childrenByParent.set(entity.parentEntityId, list);
    } else {
      roots.push(entity);
    }
  }
  const rows: TreeRow[] = [];
  const visit = (entity: LegalEntity, depth: number) => {
    rows.push({ entity, depth });
    const children = [...(childrenByParent.get(entity.id) ?? [])].sort(compare);
    for (const child of children) visit(child, depth + 1);
  };
  for (const root of [...roots].sort(compare)) visit(root, 0);
  return rows;
}

const GROUP_HINT =
  'Group-qualified: connected to the group root through majority ownership (>50 %). ' +
  'Broken ownership chains disqualify an entity and everything below it.';

export function EntitiesView() {
  const { user } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { legalEntities, legalEntitiesLoading } = useAppData();

  const [formOpen, setFormOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<LegalEntity | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const entities = useMemo(() => legalEntities ?? [], [legalEntities]);
  const qualifications = useMemo(() => computeGroupQualification(entities), [entities]);

  const { sortKey, sortDirection, toggleSort } = useTableSort<EntitySortKey>('name', 'asc');

  const rows = useMemo(() => {
    const accessors: Record<EntitySortKey, (entity: LegalEntity) => string | number> = {
      name: (entity) => entity.name,
      legalForm: (entity) => entity.legalForm,
      seat: (entity) => entity.seat ?? '',
      ownership: (entity) => entity.ownershipBps ?? 10001,
      group: (entity) => {
        const qualification: GroupQualification | undefined = qualifications[entity.id];
        return qualification?.qualified ? 1 : 0;
      },
    };
    const accessor = accessors[sortKey];
    return buildTreeRows(entities, (a, b) =>
      compareSortValues(accessor(a), accessor(b), sortDirection)
    );
  }, [entities, qualifications, sortKey, sortDirection]);

  const openCreate = useCallback(() => {
    setEditingEntity(null);
    setSaveError(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((entity: LegalEntity) => {
    setEditingEntity(entity);
    setSaveError(null);
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    if (saving) return;
    setFormOpen(false);
    setEditingEntity(null);
    setSaveError(null);
  }, [saving]);

  async function handleSubmit(values: LegalEntityFormValues) {
    if (!user?.uid || saving) return;
    setSaveError(null);
    setSaving(true);
    try {
      await saveLegalEntity(user.uid, {
        id: editingEntity?.id,
        ...values,
        existing: editingEntity
          ? { createdAt: editingEntity.createdAt, archivedAt: editingEntity.archivedAt ?? null }
          : undefined,
      });
      setFormOpen(false);
      setEditingEntity(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save entity.');
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive() {
    if (!user?.uid || !editingEntity || saving) return;
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        'Archive this entity? It will be hidden from views but kept in the database.'
      )
    ) {
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      await archiveLegalEntity(user.uid, editingEntity.id);
      setFormOpen(false);
      setEditingEntity(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to archive entity.');
    } finally {
      setSaving(false);
    }
  }

  const plusIcon = (
    <img
      src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const pencilIcon = (
    <img
      src={isDarkMode ? '/assets/icons/pencil.svg' : '/assets/icons/pencil.black.svg'}
      alt=""
      width={12}
      height={12}
    />
  );

  const toolbarActions = (
    <GlobalButton
      type="button"
      variant="secondary"
      className={styles.tapTarget}
      disabled={saving}
      onClick={openCreate}
      icon={plusIcon}
    >
      Add entity
    </GlobalButton>
  );

  return (
    <ViewPage title="Legal entities" actions={toolbarActions}>
      <ViewSectionHeader
        eyebrow="Legal entities"
        title="Group structure"
        subtitle="Manage the legal entities of your group and their ownership chain."
      />

      <section className={styles.root}>
        {legalEntitiesLoading ? (
          <p className="muted">Loading legal entities…</p>
        ) : rows.length > 0 ? (
          <>
            <div className="table-card">
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <GlobalSortableTh sortKey="name" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Entity
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="legalForm" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Legal form
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="seat" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Seat
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="ownership" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Ownership
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="group" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Group status
                      </GlobalSortableTh>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ entity, depth }) => {
                      const qualification = qualifications[entity.id];
                      return (
                        <tr key={entity.id}>
                          <td>
                            <span
                              className={styles.treeIndent}
                              style={{ paddingLeft: depth * 20 }}
                            >
                              {depth > 0 ? <span className={styles.treeBranch}>└</span> : null}
                              <strong>{entity.name}</strong>
                            </span>
                          </td>
                          <td>{entity.legalForm}</td>
                          <td className="muted">{entity.seat || '—'}</td>
                          <td>
                            {entity.parentEntityId && entity.ownershipBps != null
                              ? formatBpsAsPercent(entity.ownershipBps)
                              : 'Root'}
                          </td>
                          <td>
                            <span
                              className={
                                qualification?.qualified
                                  ? styles.groupBadge
                                  : styles.groupBadgeNeutral
                              }
                              title={GROUP_HINT}
                            >
                              {qualification?.qualified ? 'Group-qualified' : 'Not qualified'}
                            </span>
                          </td>
                          <td>
                            <GlobalMicroButton
                              type="button"
                              
                              icon={pencilIcon}
                              onClick={() => openEdit(entity)}
                            >
                              Edit
                            </GlobalMicroButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <p className={`muted ${styles.hint}`}>{GROUP_HINT}</p>
          </>
        ) : (
          <GlobalEmptyState
            buttonText="Add entity"
            onButtonClick={openCreate}
            buttonIcon={plusIcon}
          />
        )}
      </section>

      <GlobalSidebar isOpen={formOpen} onClose={closeForm}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">
            {editingEntity ? 'Edit legal entity' : 'Add legal entity'}
          </h2>
          {formOpen ? (
            <LegalEntityForm
              key={editingEntity?.id ?? 'create'}
              saving={saving}
              error={saveError}
              entities={entities}
              entity={editingEntity}
              onSubmit={handleSubmit}
              onArchive={editingEntity ? handleArchive : undefined}
              onCancel={closeForm}
            />
          ) : null}
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
